// Generating augmented images (backend/app/api/augment.py): stored copies of a project's reviewed images.
import { api } from "./client";
import type { AugmentConfig } from "./ml";

/** Which reviewed images to copy: these ids, else this folder ("" = no folder), else all of them. */
export interface AugmentSources { image_ids?: number[] | null; folder?: string | null }

export interface AugmentEstimate {
  source_images: number; copies: number; total: number; bytes_estimate: number;
  storage_used: number; storage_allowance: number; storage_fits: boolean; metered: boolean;
  credits_per_image?: number; credits?: number; balance?: number;
  /** The most one run makes (backend/app/ml/augment_images.py MAX_RUN_IMAGES), and whether this run is over it. Absent from older servers. */
  max_total?: number; too_many?: boolean;
}

export interface AugmentJob {
  id: number; project_id: number; status: "queued" | "running" | "completed" | "failed" | "cancelled";
  copies: number; total: number; done: number; folder: string; message: string; cancel_requested: boolean;
  recipe: AugmentConfig; source_images: number; created_at: string | null; finished_at: string | null;
  /** Copies with their annotations moved, or background pictures with the objects erased. Absent from older servers (copies). */
  kind?: "augment" | "background";
}

/** What a "Make background pictures" run would make and cost (backend/app/ml/background_images.py). */
export interface BackgroundEstimate {
  /** The Train page's rule: pictures with objects, backgrounds wanted for them, backgrounds the project has, and the number to make now. */
  with_objects: number; wanted: number; have: number; suggested: number;
  /** Reviewed originals it could erase from, the number asked for, the pictures it would use, what it would make, and how many of those are crops. */
  candidates: number; count: number; source_images: number; total: number; crops: number;
  max_total: number; too_many: boolean; bytes_estimate: number; storage_used: number; storage_allowance: number;
  storage_fits: boolean; metered: boolean; credits_per_image?: number; credits?: number; balance?: number;
}
export interface BackgroundPreview { original: string; erased: string; crop: string | null; image_id: number; filename: string }

const sourceQuery = (s: AugmentSources) => ({
  image_ids: s.image_ids?.length ? s.image_ids.join(",") : undefined,
  folder: s.image_ids?.length || s.folder === null || s.folder === undefined ? undefined : s.folder,
});

export const estimate = (pid: number, copies: number, s: AugmentSources) =>
  api.get<AugmentEstimate>(`/projects/${pid}/augment-images/estimate`, { query: { copies, ...sourceQuery(s) } });

export const start = (pid: number, body: AugmentSources & { augmentation: AugmentConfig; copies: number; output_folder?: string; preset_name?: string }) =>
  api.post<AugmentJob>(`/projects/${pid}/augment-images`, { json: body });

export const listJobs = (pid: number) => api.get<AugmentJob[]>(`/projects/${pid}/augment-jobs`);
export const cancelJob = (pid: number, id: number) => api.post<AugmentJob>(`/projects/${pid}/augment-jobs/${id}/cancel`);
export const deleteJob = (pid: number, id: number) => api.del<{ deleted_images: number }>(`/projects/${pid}/augment-jobs/${id}`);

/** What a background run of `count` pictures (null: the suggested number) would make from these sources. */
export const backgroundEstimate = (pid: number, count: number | null, s: AugmentSources) =>
  api.get<BackgroundEstimate>(`/projects/${pid}/background-images/estimate`, { query: { count: count ?? undefined, ...sourceQuery(s) } });
/** One reviewed picture with its objects erased (and its crop), before anything is saved. */
export const backgroundPreview = (pid: number, imageId: number | null) =>
  api.post<BackgroundPreview>(`/projects/${pid}/background-images/preview`, { json: { image_id: imageId } });
export const startBackground = (pid: number, body: AugmentSources & { count: number; output_folder?: string }) =>
  api.post<AugmentJob>(`/projects/${pid}/background-images`, { json: body });
